import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { format, parseISO } from 'date-fns';
import { useStores } from '@/hooks';
import { formatPrice } from '@pro-sto/shared/utils';
import './PendingBookingsListModal.css';

export interface PendingBookingsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (uuid: string) => Promise<void>;
  onCancel: (uuid: string) => Promise<void>;
}

const formatTime = (time: string): string => {
  return format(parseISO(time), 'HH:mm');
};

export const PendingBookingsListModal: React.FC<PendingBookingsListModalProps> = observer(({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
}) => {
  const { bookingsStore } = useStores();
  // uuid-ы записей, которые сейчас обрабатываются — блокируют только свои кнопки,
  // остальные записи в списке остаются кликабельными. Набор, а не одно значение:
  // иначе обработка двух разных записей подряд (пока первая ещё не ответила) сбрасывала
  // чужой processingUuid и снимала блокировку с ещё не завершённого запроса, позволяя
  // кликнуть по той же записи повторно и отправить updateBookingStatus дважды.
  const [processingUuids, setProcessingUuids] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const bookings = bookingsStore.pendingBookings;

  const withProcessing = async (uuid: string, action: (uuid: string) => Promise<void>) => {
    setProcessingUuids((prev) => new Set(prev).add(uuid));
    try {
      await action(uuid);
    } finally {
      setProcessingUuids((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  const handleConfirm = (uuid: string) => withProcessing(uuid, onConfirm);
  const handleCancel = (uuid: string) => withProcessing(uuid, onCancel);

  return (
    <div className="pending-bookings-list-modal__overlay" onClick={onClose} data-testid="pending-bookings-list-modal">
      <div className="pending-bookings-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pending-bookings-list-modal__header">
          <h2 className="pending-bookings-list-modal__title">
            Новые записи {bookings.length > 0 && `(${bookings.length})`}
          </h2>
          <button
            className="pending-bookings-list-modal__close"
            onClick={onClose}
            aria-label="Закрыть"
            type="button"
            data-testid="pending-bookings-list-close"
          >
            ×
          </button>
        </div>

        <div className="pending-bookings-list-modal__body">
          {bookings.map((booking) => {
            const vehicleName = booking.car ? `${booking.car.make} ${booking.car.model}` : 'Без автомобиля';
            const clientName = booking.client?.name || 'Клиент';
            const isProcessing = processingUuids.has(booking.uuid);

            return (
              <div
                key={booking.uuid}
                className="pending-bookings-list-modal__item"
                data-testid={`pending-booking-item-${booking.uuid}`}
              >
                <div className="pending-bookings-list-modal__item-info">
                  <div className="pending-bookings-list-modal__item-top">
                    <span className="pending-bookings-list-modal__vehicle">{vehicleName}</span>
                    <span className="pending-bookings-list-modal__price">{formatPrice(booking.total_cost)}₽</span>
                  </div>
                  <div className="pending-bookings-list-modal__item-bottom">
                    <span className="pending-bookings-list-modal__service">{booking.service.name}</span>
                    <span className="pending-bookings-list-modal__dot" aria-hidden="true">•</span>
                    <span className="pending-bookings-list-modal__client">{clientName}</span>
                    <span className="pending-bookings-list-modal__dot" aria-hidden="true">•</span>
                    <span className="pending-bookings-list-modal__time">{formatTime(booking.start_time)}</span>
                  </div>
                </div>

                <div className="pending-bookings-list-modal__actions">
                  <button
                    className="pending-bookings-list-modal__cancel-button"
                    onClick={() => handleCancel(booking.uuid)}
                    disabled={isProcessing}
                    type="button"
                    data-testid={`pending-booking-cancel-${booking.uuid}`}
                  >
                    Отменить
                  </button>
                  <button
                    className="pending-bookings-list-modal__confirm-button"
                    onClick={() => handleConfirm(booking.uuid)}
                    disabled={isProcessing}
                    type="button"
                    data-testid={`pending-booking-confirm-${booking.uuid}`}
                  >
                    Подтвердить
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

PendingBookingsListModal.displayName = 'PendingBookingsListModal';

export default PendingBookingsListModal;
